import React, { useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useDispatch } from 'react-redux';
import { addjobgraphland } from '../../../FeatureRedux/ProjectionReducer/JobgraphlandReducer/jobgraphland';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const initialData = {
    labels: ['2020', '2021', '2022', '2023', '2024', '2025'],
    datasets: [
        {
            label: 'Land Price (per sq.ft)',
            data: [10000, 12000, 14000, 16500, 19000, 22000],
            fill: false,
            borderColor: '#4F46E5',
            tension: 0.3,
            pointBackgroundColor: '#4F46E5',
            pointRadius: 5,
            pointHoverRadius: 7,
        },
    ],
};

const options = {
    responsive: true,
    plugins: {
        legend: {
            display: false
        },
        title: {
            display: true,
            text: 'Year-wise Land Price Trend',
        },
        tooltip: {
            callbacks: {
                label: function(context) {
                    const pricePerSqft = context.raw;
                    const pricePerAcre = (pricePerSqft * 43560).toLocaleString('en-IN');
                    const pricePerHectare = (pricePerSqft * 107639).toLocaleString('en-IN');
                    return [
                        `₹${pricePerSqft.toLocaleString('en-IN')} per sq.ft`,
                        `≈ ₹${pricePerAcre} per acre`,
                        `≈ ₹${pricePerHectare} per hectare`
                    ];
                }
            }
        }
    },
    scales: {
        y: {
            ticks: {
                callback: function(value) {
                    return '₹' + value.toLocaleString('en-IN');
                }
            }
        }
    }
};

function FutureGraph_ProjectDetail({projectiondataID ,locations}) {
    const [chartData, setChartData] = useState(initialData);
    const [showForm, setShowForm] = useState(false);
    const [newDataPoint, setNewDataPoint] = useState({
        year: '',
        price: '',
        areaUnit: 'sqft', // Default to square feet
        areaValue: '1'    // Default to 1 unit
    });
    const [editingIndex, setEditingIndex] = useState(null);
    const [shake, setShake] = useState(false);
    const [showDataTable, setShowDataTable] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(null);
    const dispatch = useDispatch();

    const handleAddClick = () => {
        setEditingIndex(null);
        setShowForm(true);
    };

    React.useEffect(() => {
        if (locations && locations.land_price_predection && Array.isArray(locations.land_price_predection)) {
            const yearDataMap = {};
    
            locations.land_price_predection.forEach(item => {
                const year = item.year.toString();
                let price = parseFloat(item.price) || 0;
                
                // Convert to price per sq.ft based on area unit
                if (item.areaUnit === 'acre') {
                    price = price / 43560; // Convert acre to sq.ft
                } else if (item.areaUnit === 'hectare') {
                    price = price / 107639; // Convert hectare to sq.ft
                } else if (item.areaUnit === 'sqyd') {
                    price = price / 9; // Convert sq.yd to sq.ft
                }
                
                // Adjust for area value
                price = price / (parseFloat(item.areaValue) || 1);

                if (yearDataMap[year]) {
                    yearDataMap[year] = price; // Keep the latest value for the year
                } else {
                    yearDataMap[year] = price;
                }
            });
    
            const labels = Object.keys(yearDataMap).sort();
            const data = labels.map(year => yearDataMap[year]);
    
            setChartData({
                labels,
                datasets: [
                    {
                        label: 'Land Price Prediction',
                        data,
                    },
                ],
            });
        }
    }, [locations]);
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewDataPoint(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const validateForm = () => {
        const yearNum = parseInt(newDataPoint.year);
        const priceNum = parseFloat(newDataPoint.price);
        const areaValueNum = parseFloat(newDataPoint.areaValue);
        return (
            newDataPoint.year &&
            !isNaN(yearNum) &&
            newDataPoint.price &&
            !isNaN(priceNum) &&
            newDataPoint.areaValue &&
            !isNaN(areaValueNum) &&
            areaValueNum > 0
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validateForm()) {
            setShake(true);
            setTimeout(() => setShake(false), 500);
            toast.error('Please fill all fields correctly!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
            return;
        }

        const apitotarget = 'land_price';
        let pricePerSqft = parseFloat(newDataPoint.price);
        const areaValue = parseFloat(newDataPoint.areaValue) || 1;
        
        // Convert to price per sq.ft based on area unit
        if (newDataPoint.areaUnit === 'acre') {
            pricePerSqft = pricePerSqft / 43560; // Convert acre to sq.ft
        } else if (newDataPoint.areaUnit === 'hectare') {
            pricePerSqft = pricePerSqft / 107639; // Convert hectare to sq.ft
        } else if (newDataPoint.areaUnit === 'sqyd') {
            pricePerSqft = pricePerSqft / 9; // Convert sq.yd to sq.ft
        }
        
        // Adjust for area value
        pricePerSqft = pricePerSqft / areaValue;

        if (editingIndex !== null) {
            // Edit existing data point
            const updatedLabels = [...chartData.labels];
            updatedLabels[editingIndex] = newDataPoint.year;
            
            const updatedDatasetData = [...chartData.datasets[0].data];
            updatedDatasetData[editingIndex] = pricePerSqft;

            setChartData({
                labels: updatedLabels,
                datasets: [
                    {
                        ...chartData.datasets[0],
                        data: updatedDatasetData
                    }
                ]
            });

            toast.success('Data point updated successfully!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        } else {
            // Add new data point
            if (chartData.labels.includes(newDataPoint.year)) {
                toast.error('Year already exists!', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                return;
            }

            dispatch(addjobgraphland({ 
                newDataPoint: {
                    ...newDataPoint,
                    price: pricePerSqft
                }, 
                projectiondataID, 
                apitotarget 
            }));

            const newLabels = [...chartData.labels, newDataPoint.year];
            const newDatasetData = [...chartData.datasets[0].data, pricePerSqft];

            setChartData({
                labels: newLabels,
                datasets: [
                    {
                        ...chartData.datasets[0],
                        data: newDatasetData
                    }
                ]
            });

            toast.success('Data point added successfully!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }

        setNewDataPoint({ year: '', price: '', areaUnit: 'sqft', areaValue: '1' });
        setShowForm(false);
        setEditingIndex(null);
    };

    const handleCancel = (e) => {
        if (e.target === e.currentTarget) {
            setNewDataPoint({ year: '', price: '', areaUnit: 'sqft', areaValue: '1' });
            setShowForm(false);
            setEditingIndex(null);
        }
    };

    const handleEdit = (index) => {
        const pricePerSqft = chartData.datasets[0].data[index];
        // For editing, we'll show the price as per sq.ft (1 unit)
        setEditingIndex(index);
        setNewDataPoint({
            year: chartData.labels[index],
            price: pricePerSqft,
            areaUnit: 'sqft',
            areaValue: '1'
        });
        setShowForm(true);
        setShowDataTable(false);
    };

    const handleDeleteClick = (index) => {
        setDeleteIndex(index);
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = (confirmed) => {
        if (confirmed) {
            const updatedLabels = chartData.labels.filter((_, i) => i !== deleteIndex);
            const updatedDatasetData = chartData.datasets[0].data.filter((_, i) => i !== deleteIndex);

            setChartData({
                labels: updatedLabels,
                datasets: [
                    {
                        ...chartData.datasets[0],
                        data: updatedDatasetData
                    }
                ]
            });

            toast.success('Data point deleted successfully!', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
            });
        }
        setShowDeleteConfirm(false);
        setDeleteIndex(null);
    };

    const toggleDataTable = () => {
        setShowDataTable(!showDataTable);
    };

    const formatPrice = (pricePerSqft) => {
        const pricePerAcre = (pricePerSqft * 43560).toLocaleString('en-IN');
        const pricePerHectare = (pricePerSqft * 107639).toLocaleString('en-IN');
        const pricePerSqyd = (pricePerSqft * 9).toLocaleString('en-IN');
        
        return {
            perSqft: `₹${pricePerSqft.toLocaleString('en-IN')}`,
            perAcre: `₹${pricePerAcre}`,
            perHectare: `₹${pricePerHectare}`,
            perSqyd: `₹${pricePerSqyd}`
        };
    };

    return (
        <div className="max-w-2xl mx-auto p-4 relative">
            <div className="relative flex items-center mb-4">
                <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
                    Land Price Prediction
                </h3>
                <div className="ml-auto flex space-x-2">
                    <button
                        onClick={toggleDataTable}
                        className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-full transition-all"
                        title="View Data Table"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </button>
                    <button
                        onClick={handleAddClick}
                        className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
                        title="Add Land Price Prediction"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>  

          

            <Line data={chartData} options={options} />

            {/* Data Table Modal */}
            {showDataTable && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300">
                    <div className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto border border-gray-700 shadow-2xl">
                        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-white">Land Price Data Points</h3>
                            <button
                                onClick={toggleDataTable}
                                className="text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-700">
                                <thead className="bg-gray-700">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                            Year
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                            Price/sq.ft
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                            Per Acre
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                            Per Hectare
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-gray-800 divide-y divide-gray-700">
                                    {chartData.labels.map((label, index) => {
                                        const prices = formatPrice(chartData.datasets[0].data[index]);
                                        return (
                                            <tr key={index} className="hover:bg-gray-750">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-left">
                                                    {label}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-left">
                                                    {prices.perSqft}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-left">
                                                    {prices.perAcre}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-left">
                                                    {prices.perHectare}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <div className="flex justify-end space-x-2">
                                                        <button
                                                            onClick={() => handleEdit(index)}
                                                            className="text-blue-400 hover:text-blue-300 p-1 rounded-full hover:bg-gray-700"
                                                            title="Edit"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(index)}
                                                            className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-gray-700"
                                                            title="Delete"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
                    <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 shadow-2xl">
                        <div className="mb-4">
                            <h3 className="text-lg font-semibold text-white">Confirm Deletion</h3>
                            <p className="text-gray-300 mt-2">
                                Are you sure you want to delete land price data for {chartData.labels[deleteIndex]}?
                            </p>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => handleDeleteConfirm(false)}
                                className="px-4 py-2 border border-gray-600 rounded-lg text-sm font-medium text-white bg-transparent hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDeleteConfirm(true)}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add/Edit Form Modal */}
            {showForm && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300"
                    onClick={handleCancel}
                >
                    <div
                        className={`bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-xl w-full max-w-md relative border border-gray-700 shadow-2xl transform transition-all duration-300 ${shake ? 'animate-shake' : ''}`}
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowForm(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="mb-6 text-center">
                            <h3 className="text-2xl font-bold text-white mb-1">
                                {editingIndex !== null ? 'Edit Land Price' : 'Add Land Price'}
                            </h3>
                            <p className="text-gray-400 text-sm">
                                {editingIndex !== null ? 'Update the selected data point' : 'Enter land price details'}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="flex flex-col">
                                <label className="text-gray-300 text-sm font-medium mb-1 text-left">Year</label>
                                <input
                                    type="number"
                                    name="year"
                                    value={newDataPoint.year}
                                    onChange={handleInputChange}
                                    className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                    placeholder="e.g., 2026"
                                />
                            </div>

                            <div className="flex flex-col">
                                <label className="text-gray-300 text-sm font-medium mb-1 text-left">Price</label>
                                <div className="flex">
                                    <input
                                        type="number"
                                        name="price"
                                        value={newDataPoint.price}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-l-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none"
                                        placeholder="Enter price"
                                    />
                                    <select
                                        name="areaUnit"
                                        value={newDataPoint.areaUnit}
                                        onChange={handleInputChange}
                                        className="bg-gray-700 border border-gray-600 border-l-0 text-white px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="sqft">per sq.ft</option>
                                        <option value="sqyd">per sq.yd</option>
                                        <option value="acre">per acre</option>
                                        <option value="hectare">per hectare</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex flex-col">
                                <label className="text-gray-300 text-sm font-medium mb-1 text-left">Area Value (optional)</label>
                                <input
                                    type="number"
                                    name="areaValue"
                                    value={newDataPoint.areaValue}
                                    onChange={handleInputChange}
                                    className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                    placeholder="e.g., 2 (for 2 acres)"
                                    min="0.01"
                                    step="0.01"
                                />
                                <p className="text-xs text-gray-400 mt-1">Leave as 1 for single unit price</p>
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="px-4 py-2 border border-gray-600 rounded-lg text-sm font-medium text-white bg-transparent hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all shadow-lg hover:shadow-blue-500/30"
                                >
                                    {editingIndex !== null ? 'Update Price' : 'Add Price'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FutureGraph_ProjectDetail;
// import React, { useState } from 'react';
// import {
//     Chart as ChartJS,
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend,
// } from 'chart.js';
// import { Line } from 'react-chartjs-2';
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { useDispatch } from 'react-redux';
// import { addjobgraphland } from '../../../FeatureRedux/ProjectionReducer/JobgraphlandReducer/jobgraphland';

// ChartJS.register(
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend
// );

// const initialData = {
//     labels: ['2020', '2021', '2022', '2023', '2024', '2025'],
//     datasets: [
//         {
//             label: 'Cost Increase (₹)',
//             data: [10000, 12000, 14000, 16500, 19000, 22000],
//             fill: false,
//             borderColor: '#4F46E5',
//             tension: 0.3,
//             pointBackgroundColor: '#4F46E5',
//             pointRadius: 5,
//             pointHoverRadius: 7,
//         },
//     ],
// };

// const options = {
//     responsive: true,
//     plugins: {
//         legend: {
//             display: false // This hides the legend with the blue box
//         },
//         title: {
//             display: true,
//             text: 'Year-wise Cost Increase',
//         },
//     },
// };

// function FutureGraph_ProjectDetail({projectiondataID ,locations}) {
//     const [chartData, setChartData] = useState(initialData);
//     const [showForm, setShowForm] = useState(false);
//     const [newDataPoint, setNewDataPoint] = useState({
//         year: '',
//         count: ''
//     });
//     const [editingIndex, setEditingIndex] = useState(null);
//     const [shake, setShake] = useState(false);
//     const [showDataTable, setShowDataTable] = useState(false);
//     const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
//     const [deleteIndex, setDeleteIndex] = useState(null);
//     const dispatch = useDispatch();

//     const handleAddClick = () => {
//         setEditingIndex(null);
//         setShowForm(true);
//     };

  
//     React.useEffect(() => {
//         if (locations && locations.land_price_predection && Array.isArray(locations.land_price_predection)) {
//             // Merge the data: sum counts of same year
//             const yearDataMap = {};
    
//             locations.land_price_predection.forEach(item => {
//                 const year = item.year.toString();
//                 const count = parseFloat(item.count) || 0;
    
//                 if (yearDataMap[year]) {
//                     yearDataMap[year] += count;
//                 } else {
//                     yearDataMap[year] = count;
//                 }
//             });
    
//             const labels = Object.keys(yearDataMap).sort();
//             const data = labels.map(year => yearDataMap[year]);
    
//             setChartData({
//                 labels,
//                 datasets: [
//                     {
//                         label: 'Job Impact',
//                         data,
//                         fill: false,
//                         borderColor: '#4F46E5',
//                         tension: 0.3,
//                     },
//                 ],
//             });
//         }
//     }, [locations]);
    
//     const handleInputChange = (e) => {
//         const { name, value } = e.target;
//         setNewDataPoint(prev => ({
//             ...prev,
//             [name === 'value' ? 'count' : name]: value
//         }));
//     };

//     const validateForm = () => {
//         return (
//             newDataPoint.year &&
//             newDataPoint.count &&
//             !isNaN(newDataPoint.count)
//         );
//     };

//     const handleSubmit = (e) => {
//         e.preventDefault();

//         if (!validateForm()) {
//             setShake(true);
//             setTimeout(() => setShake(false), 500);
//             toast.error('Please fill all fields correctly!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//             return;
//         }

//         const apitotarget = 'xyz';

//         if (editingIndex !== null) {
//             // Edit existing data point
//             const updatedLabels = [...chartData.labels];
//             updatedLabels[editingIndex] = newDataPoint.year;
            
//             const updatedDatasetData = [...chartData.datasets[0].data];
//             updatedDatasetData[editingIndex] = parseFloat(newDataPoint.count);

//             setChartData({
//                 labels: updatedLabels,
//                 datasets: [
//                     {
//                         ...chartData.datasets[0],
//                         data: updatedDatasetData
//                     }
//                 ]
//             });

//             toast.success('Data point updated successfully!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//         } else {
//             // Add new data point
//             dispatch(addjobgraphland({ newDataPoint, projectiondataID, apitotarget }));

//             const newLabels = [...chartData.labels, newDataPoint.year];
//             const newDatasetData = [...chartData.datasets[0].data, parseFloat(newDataPoint.count)];

//             setChartData({
//                 labels: newLabels,
//                 datasets: [
//                     {
//                         ...chartData.datasets[0],
//                         data: newDatasetData
//                     }
//                 ]
//             });

//             toast.success('Data point added successfully!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//         }

//         setNewDataPoint({ year: '', count: '' });
//         setShowForm(false);
//         setEditingIndex(null);
//     };

//     const handleCancel = (e) => {
//         if (e.target === e.currentTarget) {
//             setNewDataPoint({ year: '', count: '' });
//             setShowForm(false);
//             setEditingIndex(null);
//         }
//     };

//     const handleEdit = (index) => {
//         setEditingIndex(index);
//         setNewDataPoint({
//             year: chartData.labels[index],
//             count: chartData.datasets[0].data[index]
//         });
//         setShowForm(true);
//         setShowDataTable(false);
//     };

//     const handleDeleteClick = (index) => {
//         setDeleteIndex(index);
//         setShowDeleteConfirm(true);
//     };

//     const handleDeleteConfirm = (confirmed) => {
//         if (confirmed) {
//             const updatedLabels = chartData.labels.filter((_, i) => i !== deleteIndex);
//             const updatedDatasetData = chartData.datasets[0].data.filter((_, i) => i !== deleteIndex);

//             setChartData({
//                 labels: updatedLabels,
//                 datasets: [
//                     {
//                         ...chartData.datasets[0],
//                         data: updatedDatasetData
//                     }
//                 ]
//             });

//             toast.success('Data point deleted successfully!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//         }
//         setShowDeleteConfirm(false);
//         setDeleteIndex(null);
//     };

//     const toggleDataTable = () => {
//         setShowDataTable(!showDataTable);
//     };

//     return (
//         <div className="max-w-2xl mx-auto p-4 relative">
//             <div className="relative flex items-center mb-4">
//                 <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//                     Land Price Prediction
//                 </h3>
//                 <div className="ml-auto flex space-x-2">
//                     <button
//                         onClick={toggleDataTable}
//                         className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-full transition-all"
//                         title="View Data Table"
//                     >
//                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
//                         </svg>
//                     </button>
//                     <button
//                         onClick={handleAddClick}
//                         className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//                         title="Add Land Price Prediction"
//                     >
//                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//                             <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//                         </svg>
//                     </button>
//                 </div>
//             </div>

//             <Line data={chartData} options={options} />

//             {/* Data Table Modal */}
//             {showDataTable && (
//                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300">
//                     <div className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto border border-gray-700 shadow-2xl">
//                         <div className="p-4 border-b border-gray-700 flex justify-between items-center">
//                             <h3 className="text-lg font-semibold text-white">Land Price Data Points</h3>
//                             <button
//                                 onClick={toggleDataTable}
//                                 className="text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
//                             >
//                                 <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                                 </svg>
//                             </button>
//                         </div>
//                         <div className="overflow-x-auto">
//                             <table className="min-w-full divide-y divide-gray-700">
//                                 <thead className="bg-gray-700">
//                                     <tr>
//                                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                                             Year
//                                         </th>
//                                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                                             Projected Cost (₹)
//                                         </th>
//                                         <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
//                                             Actions
//                                         </th>
//                                     </tr>
//                                 </thead>
//                                 <tbody className="bg-gray-800 divide-y divide-gray-700">
//                                     {chartData.labels.map((label, index) => (
//                                         <tr key={index} className="hover:bg-gray-750">
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-left">
//                                                 {label}
//                                             </td>
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-left">
//                                                 {chartData.datasets[0].data[index].toLocaleString()}
//                                             </td>
//                                             <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                                 <div className="flex justify-end space-x-2">
//                                                     <button
//                                                         onClick={() => handleEdit(index)}
//                                                         className="text-blue-400 hover:text-blue-300 p-1 rounded-full hover:bg-gray-700"
//                                                         title="Edit"
//                                                     >
//                                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
//                                                         </svg>
//                                                     </button>
//                                                     <button
//                                                         onClick={() => handleDeleteClick(index)}
//                                                         className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-gray-700"
//                                                         title="Delete"
//                                                     >
//                                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
//                                                         </svg>
//                                                     </button>
//                                                 </div>
//                                             </td>
//                                         </tr>
//                                     ))}
//                                 </tbody>
//                             </table>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* Delete Confirmation Modal */}
//             {showDeleteConfirm && (
//                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
//                     <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 shadow-2xl">
//                         <div className="mb-4">
//                             <h3 className="text-lg font-semibold text-white">Confirm Deletion</h3>
//                             <p className="text-gray-300 mt-2">
//                                 Are you sure you want to delete land price data for {chartData.labels[deleteIndex]}?
//                             </p>
//                         </div>
//                         <div className="flex justify-end space-x-3">
//                             <button
//                                 onClick={() => handleDeleteConfirm(false)}
//                                 className="px-4 py-2 border border-gray-600 rounded-lg text-sm font-medium text-white bg-transparent hover:bg-gray-700 transition-colors"
//                             >
//                                 Cancel
//                             </button>
//                             <button
//                                 onClick={() => handleDeleteConfirm(true)}
//                                 className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
//                             >
//                                 Delete
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* Add/Edit Form Modal */}
//             {showForm && (
//                 <div
//                     className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300"
//                     onClick={handleCancel}
//                 >
//                     <div
//                         className={`bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-xl w-full max-w-md relative border border-gray-700 shadow-2xl transform transition-all duration-300 ${shake ? 'animate-shake' : ''}`}
//                         onClick={e => e.stopPropagation()}
//                     >
//                         <button
//                             onClick={() => setShowForm(false)}
//                             className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
//                         >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                             </svg>
//                         </button>

//                         <div className="mb-6 text-center">
//                             <h3 className="text-2xl font-bold text-white mb-1">
//                                 {editingIndex !== null ? 'Edit Land Price Prediction' : 'Add Land Price Prediction'}
//                             </h3>
//                             <p className="text-gray-400 text-sm">
//                                 {editingIndex !== null ? 'Update the selected data point' : 'Fill in the details to add to the chart'}
//                             </p>
//                         </div>

//                         <form onSubmit={handleSubmit} className="space-y-6">
//                             <div className="flex flex-col">
//                                 <label className="text-gray-300 text-sm font-medium mb-1 text-left">Year</label>
//                                 <input
//                                     type="number"
//                                     name="year"
//                                     value={newDataPoint.year}
//                                     onChange={handleInputChange}
//                                     className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
//                                     placeholder="e.g., 2026"
//                                 />
//                             </div>

//                             <div className="flex flex-col">
//                                 <label className="text-gray-300 text-sm font-medium mb-1 text-left">Projected Cost</label>
//                                 <div className="relative">
//                                     <input
//                                         type="number"
//                                         name="count"
//                                         value={newDataPoint.count}
//                                         onChange={handleInputChange}
//                                         className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none"
//                                         placeholder="Enter projected cost"
//                                     />
//                                     <span className="absolute right-4 top-3 text-gray-400">₹</span>
//                                 </div>
//                             </div>

//                             <div className="flex justify-end space-x-3">
//                                 <button
//                                     type="button"
//                                     onClick={() => setShowForm(false)}
//                                     className="px-4 py-2 border border-gray-600 rounded-lg text-sm font-medium text-white bg-transparent hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
//                                 >
//                                     Cancel
//                                 </button>
//                                 <button
//                                     type="submit"
//                                     className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all shadow-lg hover:shadow-blue-500/30"
//                                 >
//                                     {editingIndex !== null ? 'Update Projection' : 'Add Projection'}
//                                 </button>
//                             </div>
//                         </form>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// }

// export default FutureGraph_ProjectDetail;
// import React, { useState } from 'react';
// import {
//     Chart as ChartJS,
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend,
// } from 'chart.js';
// import { Line } from 'react-chartjs-2';
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { useDispatch } from 'react-redux';
// import { addjobgraphland } from '../../../FeatureRedux/ProjectionReducer/JobgraphlandReducer/jobgraphland';

// ChartJS.register(
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend
// );

// const initialData = {
//     labels: ['2020', '2021', '2022', '2023', '2024', '2025'],
//     datasets: [
//         {
//             label: 'Cost Increase (₹)',
//             data: [10000, 12000, 14000, 16500, 19000, 22000],
//             fill: false,
//             borderColor: '#4F46E5',
//             tension: 0.3,
//             pointBackgroundColor: '#4F46E5',
//             pointRadius: 5,
//             pointHoverRadius: 7,
//         },
//     ],
// };

// const options = {
//     responsive: true,
//     plugins: {
//         legend: {
//             position: 'top',
//         },
//         title: {
//             display: true,
//             text: 'Year-wise Cost Increase',
//         },
//     },
// };

// function FutureGraph_ProjectDetail({projectiondataID ,locations}) {
//     const [chartData, setChartData] = useState(initialData);
//     const [showForm, setShowForm] = useState(false);
//     const [newDataPoint, setNewDataPoint] = useState({
//         year: '',
//         count: ''
//     });
//     const [editingIndex, setEditingIndex] = useState(null);
//     const [shake, setShake] = useState(false);
//     const [showDataTable, setShowDataTable] = useState(false);
//     const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
//     const [deleteIndex, setDeleteIndex] = useState(null);
//     const dispatch = useDispatch();

//     const handleAddClick = () => {
//         setEditingIndex(null);
//         setShowForm(true);
//     };

  
//     React.useEffect(() => {
//         if (locations && locations.land_price_predection && Array.isArray(locations.land_price_predection)) {
//             // Merge the data: sum counts of same year
//             const yearDataMap = {};
    
//             locations.land_price_predection.forEach(item => {
//                 const year = item.year.toString();
//                 const count = parseFloat(item.count) || 0;
    
//                 if (yearDataMap[year]) {
//                     yearDataMap[year] += count;
//                 } else {
//                     yearDataMap[year] = count;
//                 }
//             });
    
//             const labels = Object.keys(yearDataMap).sort();
//             const data = labels.map(year => yearDataMap[year]);
    
//             setChartData({
//                 labels,
//                 datasets: [
//                     {
//                         label: 'Job Impact',
//                         data,
//                         fill: false,
//                         borderColor: '#4F46E5',
//                         tension: 0.3,
//                     },
//                 ],
//             });
//         }
//     }, [locations]);
    
//     const handleInputChange = (e) => {
//         const { name, value } = e.target;
//         setNewDataPoint(prev => ({
//             ...prev,
//             [name === 'value' ? 'count' : name]: value
//         }));
//     };

//     const validateForm = () => {
//         return (
//             newDataPoint.year &&
//             newDataPoint.count &&
//             !isNaN(newDataPoint.count)
//         );
//     };

//     const handleSubmit = (e) => {
//         e.preventDefault();

//         if (!validateForm()) {
//             setShake(true);
//             setTimeout(() => setShake(false), 500);
//             toast.error('Please fill all fields correctly!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//             return;
//         }

//         const apitotarget = 'xyz';

//         if (editingIndex !== null) {
//             // Edit existing data point
//             const updatedLabels = [...chartData.labels];
//             updatedLabels[editingIndex] = newDataPoint.year;
            
//             const updatedDatasetData = [...chartData.datasets[0].data];
//             updatedDatasetData[editingIndex] = parseFloat(newDataPoint.count);

//             setChartData({
//                 labels: updatedLabels,
//                 datasets: [
//                     {
//                         ...chartData.datasets[0],
//                         data: updatedDatasetData
//                     }
//                 ]
//             });

//             toast.success('Data point updated successfully!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//         } else {
//             // Add new data point
//             dispatch(addjobgraphland({ newDataPoint, projectiondataID, apitotarget }));

//             const newLabels = [...chartData.labels, newDataPoint.year];
//             const newDatasetData = [...chartData.datasets[0].data, parseFloat(newDataPoint.count)];

//             setChartData({
//                 labels: newLabels,
//                 datasets: [
//                     {
//                         ...chartData.datasets[0],
//                         data: newDatasetData
//                     }
//                 ]
//             });

//             toast.success('Data point added successfully!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//         }

//         setNewDataPoint({ year: '', count: '' });
//         setShowForm(false);
//         setEditingIndex(null);
//     };

//     const handleCancel = (e) => {
//         if (e.target === e.currentTarget) {
//             setNewDataPoint({ year: '', count: '' });
//             setShowForm(false);
//             setEditingIndex(null);
//         }
//     };

//     const handleEdit = (index) => {
//         setEditingIndex(index);
//         setNewDataPoint({
//             year: chartData.labels[index],
//             count: chartData.datasets[0].data[index]
//         });
//         setShowForm(true);
//         setShowDataTable(false);
//     };

//     const handleDeleteClick = (index) => {
//         setDeleteIndex(index);
//         setShowDeleteConfirm(true);
//     };

//     const handleDeleteConfirm = (confirmed) => {
//         if (confirmed) {
//             const updatedLabels = chartData.labels.filter((_, i) => i !== deleteIndex);
//             const updatedDatasetData = chartData.datasets[0].data.filter((_, i) => i !== deleteIndex);

//             setChartData({
//                 labels: updatedLabels,
//                 datasets: [
//                     {
//                         ...chartData.datasets[0],
//                         data: updatedDatasetData
//                     }
//                 ]
//             });

//             toast.success('Data point deleted successfully!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//         }
//         setShowDeleteConfirm(false);
//         setDeleteIndex(null);
//     };

//     const toggleDataTable = () => {
//         setShowDataTable(!showDataTable);
//     };

//     return (
//         <div className="max-w-2xl mx-auto p-4 relative">
//             <div className="relative flex items-center mb-4">
//                 <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//                     Land Price Prediction
//                 </h3>
//                 <div className="ml-auto flex space-x-2">
//                     <button
//                         onClick={toggleDataTable}
//                         className="bg-gray-600 hover:bg-gray-700 text-white p-2 rounded-full transition-all"
//                         title="View Data Table"
//                     >
//                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
//                         </svg>
//                     </button>
//                     <button
//                         onClick={handleAddClick}
//                         className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//                         title="Add Land Price Prediction"
//                     >
//                         <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//                             <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//                         </svg>
//                     </button>
//                 </div>
//             </div>

//             <Line data={chartData} options={options} />

//             {/* Data Table Modal */}
//             {showDataTable && (
//                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300">
//                     <div className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto border border-gray-700 shadow-2xl">
//                         <div className="p-4 border-b border-gray-700 flex justify-between items-center">
//                             <h3 className="text-lg font-semibold text-white">Land Price Data Points</h3>
//                             <button
//                                 onClick={toggleDataTable}
//                                 className="text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
//                             >
//                                 <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                                 </svg>
//                             </button>
//                         </div>
//                         <div className="overflow-x-auto">
//                             <table className="min-w-full divide-y divide-gray-700">
//                                 <thead className="bg-gray-700">
//                                     <tr>
//                                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                                             Year
//                                         </th>
//                                         <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
//                                             Projected Cost (₹)
//                                         </th>
//                                         <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
//                                             Actions
//                                         </th>
//                                     </tr>
//                                 </thead>
//                                 <tbody className="bg-gray-800 divide-y divide-gray-700">
//                                     {chartData.labels.map((label, index) => (
//                                         <tr key={index} className="hover:bg-gray-750">
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-left">
//                                                 {label}
//                                             </td>
//                                             <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200 text-left">
//                                                 {chartData.datasets[0].data[index].toLocaleString()}
//                                             </td>
//                                             <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                                                 <div className="flex justify-end space-x-2">
//                                                     <button
//                                                         onClick={() => handleEdit(index)}
//                                                         className="text-blue-400 hover:text-blue-300 p-1 rounded-full hover:bg-gray-700"
//                                                         title="Edit"
//                                                     >
//                                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
//                                                         </svg>
//                                                     </button>
//                                                     <button
//                                                         onClick={() => handleDeleteClick(index)}
//                                                         className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-gray-700"
//                                                         title="Delete"
//                                                     >
//                                                         <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
//                                                         </svg>
//                                                     </button>
//                                                 </div>
//                                             </td>
//                                         </tr>
//                                     ))}
//                                 </tbody>
//                             </table>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* Delete Confirmation Modal */}
//             {showDeleteConfirm && (
//                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm">
//                     <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md border border-gray-700 shadow-2xl">
//                         <div className="mb-4">
//                             <h3 className="text-lg font-semibold text-white">Confirm Deletion</h3>
//                             <p className="text-gray-300 mt-2">
//                                 Are you sure you want to delete land price data for {chartData.labels[deleteIndex]}?
//                             </p>
//                         </div>
//                         <div className="flex justify-end space-x-3">
//                             <button
//                                 onClick={() => handleDeleteConfirm(false)}
//                                 className="px-4 py-2 border border-gray-600 rounded-lg text-sm font-medium text-white bg-transparent hover:bg-gray-700 transition-colors"
//                             >
//                                 Cancel
//                             </button>
//                             <button
//                                 onClick={() => handleDeleteConfirm(true)}
//                                 className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
//                             >
//                                 Delete
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}

//             {/* Add/Edit Form Modal */}
//             {showForm && (
//                 <div
//                     className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300"
//                     onClick={handleCancel}
//                 >
//                     <div
//                         className={`bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-xl w-full max-w-md relative border border-gray-700 shadow-2xl transform transition-all duration-300 ${shake ? 'animate-shake' : ''}`}
//                         onClick={e => e.stopPropagation()}
//                     >
//                         <button
//                             onClick={() => setShowForm(false)}
//                             className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
//                         >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                             </svg>
//                         </button>

//                         <div className="mb-6 text-center">
//                             <h3 className="text-2xl font-bold text-white mb-1">
//                                 {editingIndex !== null ? 'Edit Land Price Prediction' : 'Add Land Price Prediction'}
//                             </h3>
//                             <p className="text-gray-400 text-sm">
//                                 {editingIndex !== null ? 'Update the selected data point' : 'Fill in the details to add to the chart'}
//                             </p>
//                         </div>

//                         <form onSubmit={handleSubmit} className="space-y-6">
//                             <div className="flex flex-col">
//                                 <label className="text-gray-300 text-sm font-medium mb-1 text-left">Year</label>
//                                 <input
//                                     type="number"
//                                     name="year"
//                                     value={newDataPoint.year}
//                                     onChange={handleInputChange}
//                                     className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
//                                     placeholder="e.g., 2026"
//                                 />
//                             </div>

//                             <div className="flex flex-col">
//                                 <label className="text-gray-300 text-sm font-medium mb-1 text-left">Projected Cost</label>
//                                 <div className="relative">
//                                     <input
//                                         type="number"
//                                         name="count"
//                                         value={newDataPoint.count}
//                                         onChange={handleInputChange}
//                                         className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none"
//                                         placeholder="Enter projected cost"
//                                     />
//                                     <span className="absolute right-4 top-3 text-gray-400">₹</span>
//                                 </div>
//                             </div>

//                             <div className="flex justify-end space-x-3">
//                                 <button
//                                     type="button"
//                                     onClick={() => setShowForm(false)}
//                                     className="px-4 py-2 border border-gray-600 rounded-lg text-sm font-medium text-white bg-transparent hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
//                                 >
//                                     Cancel
//                                 </button>
//                                 <button
//                                     type="submit"
//                                     className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all shadow-lg hover:shadow-blue-500/30"
//                                 >
//                                     {editingIndex !== null ? 'Update Projection' : 'Add Projection'}
//                                 </button>
//                             </div>
//                         </form>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// }

// export default FutureGraph_ProjectDetail;

// import React, { useState } from 'react';
// import {
//     Chart as ChartJS,
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend,
// } from 'chart.js';
// import { Line } from 'react-chartjs-2';
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
// import { useDispatch } from 'react-redux';
// import { addjobgraphland } from '../../../FeatureRedux/ProjectionReducer/JobgraphlandReducer/jobgraphland';

// ChartJS.register(
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend
// );

// const initialData = {
//     labels: ['2020', '2021', '2022', '2023', '2024', '2025'],
//     datasets: [
//         {
//             label: 'Cost Increase (₹)',
//             data: [10000, 12000, 14000, 16500, 19000, 22000],
//             fill: false,
//             borderColor: '#4F46E5',
//             tension: 0.3,
//         },
//     ],
// };

// const options = {
//     responsive: true,
//     plugins: {
//         legend: {
//             position: 'top',
//         },
//         title: {
//             display: true,
//             text: 'Year-wise Cost Increase',
//         },
//     },
// };

// function FutureGraph_ProjectDetail({projectiondataID}) {
//     const [chartData, setChartData] = useState(initialData);
//     const [showForm, setShowForm] = useState(false);
//     const [newDataPoint, setNewDataPoint] = useState({
//         year: '',
//         count: ''
//     });
//     const [shake, setShake] = useState(false);

//     const handleAddClick = () => {
//         setShowForm(true);
//     };
//      const dispatch = useDispatch()

  

    
//     const handleInputChange = (e) => {
//         const { name, value } = e.target;
//         setNewDataPoint(prev => ({
//             ...prev,
//             [name === 'value' ? 'count' : name]: value
//         }));
//     };


//     const validateForm = () => {
//         return (
//             newDataPoint.year &&
//             newDataPoint.count &&
//             !isNaN(newDataPoint.count)
//         );
//     };

//     const handleSubmit = (e) => {
//         e.preventDefault();
        
//         if (!validateForm()) {
//             setShake(true);
//             setTimeout(() => setShake(false), 500);
//             toast.error('Please fill all fields correctly!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//             return;
//         }
        
//         const apitotarget = 'xyz'
//         dispatch(addjobgraphland({ newDataPoint, projectiondataID, apitotarget }));

//         const newLabels = [...chartData.labels, newDataPoint.year];
//         const newDatasetData = [...chartData.datasets[0].data, parseFloat(newDataPoint.count)];
        
//         setChartData({
//             labels: newLabels,
//             datasets: [
//                 {
//                     ...chartData.datasets[0],
//                     data: newDatasetData
//                 }
//             ]
//         });
        
//         toast.success('Data point added successfully!', {
//             position: "top-right",
//             autoClose: 3000,
//             hideProgressBar: false,
//             closeOnClick: true,
//             pauseOnHover: true,
//             draggable: true,
//             progress: undefined,
//         });
        
//         setNewDataPoint({ year: '', count: '' });
//         setShowForm(false);
//     };

//     const handleCancel = (e) => {
//         if (e.target === e.currentTarget) {
//             setNewDataPoint({ year: '', count: '' });
//             setShowForm(false);
//         }
//     };

//     return (
//         <div className="max-w-2xl mx-auto p-4 relative">
//             <div className="relative flex items-center mb-4">
//                 <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//                 Land Price Prediction
//                 </h3>
//                 <button
//                     onClick={handleAddClick}
//                     className="ml-auto bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//                     title="Add Land Price Prediction"
//                 >
//                     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//                         <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//                     </svg>
//                 </button>
//             </div>

//             <Line data={chartData} options={options} />

//             {showForm && (
//                 <div 
//                     className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300"
//                     onClick={handleCancel}
//                 >
//                     <div
//                         className={`bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-xl w-full max-w-md relative border border-gray-700 shadow-2xl transform transition-all duration-300 ${shake ? 'animate-shake' : ''}`}
//                         onClick={e => e.stopPropagation()}
//                     >
//                         <button
//                             onClick={() => setShowForm(false)}
//                             className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
//                         >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                             </svg>
//                         </button>

//                         <div className="mb-6 text-center">
//                             <h3 className="text-2xl font-bold text-white mb-1">Add Land Price Prediction</h3>
//                             <p className="text-gray-400 text-sm">Fill in the details to add to the chart</p>
//                         </div>

//                         <form onSubmit={handleSubmit} className="space-y-6">
//                             <div className="flex flex-col">
//                                 <label className="text-gray-300 text-sm font-medium mb-1 text-left">Year</label>
//                                 <input
//                                     type="number"
//                                     name="year"
//                                     value={newDataPoint.year}
//                                     onChange={handleInputChange}
//                                     className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
//                                     placeholder="e.g., 2026"
//                                 />
//                             </div>

//                             <div className="flex flex-col">
//                                 <label className="text-gray-300 text-sm font-medium mb-1 text-left">Projected Cost</label>
//                                 <div className="relative">
//                                     <input
//                                         type="number"
//                                         name="count"
//                                         value={newDataPoint.count}
//                                         onChange={handleInputChange}
//                                         className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none"
//                                         placeholder="Enter projected cost"
//                                     />
//                                     <span className="absolute right-4 top-3 text-gray-400">₹</span>
//                                 </div>
//                             </div>

//                             <div className="flex justify-end space-x-3">
//                                 <button
//                                     type="button"
//                                     onClick={() => setShowForm(false)}
//                                     className="px-4 py-2 border border-gray-600 rounded-lg text-sm font-medium text-white bg-transparent hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
//                                 >
//                                     Cancel
//                                 </button>
//                                 <button
//                                     type="submit"
//                                     className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all shadow-lg hover:shadow-blue-500/30"
//                                 >
//                                     Add Projection
//                                 </button>
//                             </div>
//                         </form>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// }

// export default FutureGraph_ProjectDetail;

// import React, { useState } from 'react';
// import {
//     Chart as ChartJS,
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend,
// } from 'chart.js';
// import { Line } from 'react-chartjs-2';
// import { toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// // Register required chart components
// ChartJS.register(
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend
// );

// const initialData = {
//     labels: ['2020', '2021', '2022', '2023', '2024', '2025'],
//     datasets: [
//         {
//             label: 'Cost Increase (₹)',
//             data: [10000, 12000, 14000, 16500, 19000, 22000],
//             fill: false,
//             borderColor: '#4F46E5',
//             tension: 0.3,
//         },
//     ],
// };

// const options = {
//     responsive: true,
//     plugins: {
//         legend: {
//             position: 'top',
//         },
//         title: {
//             display: true,
//             text: 'Year-wise Cost Increase',
//         },
//     },
// };

// function FutureGraph_ProjectDetail() {
//     const [chartData, setChartData] = useState(initialData);
//     const [showForm, setShowForm] = useState(false);
//     const [newDataPoint, setNewDataPoint] = useState({
//         year: '',
//         value: ''
//     });
//     const [shake, setShake] = useState(false);

//     const handleAddClick = () => {
//         setShowForm(true);
//     };

//     const handleInputChange = (e) => {
//         const { name, value } = e.target;
//         setNewDataPoint(prev => ({
//             ...prev,
//             [name]: value
//         }));
//     };

//     const validateForm = () => {
//         return (
//             newDataPoint.year &&
//             newDataPoint.value &&
//             parseFloat(newDataPoint.value) > 0
//         );
//     };

//     const handleSubmit = (e) => {
//         e.preventDefault();
        
//         if (!validateForm()) {
//             setShake(true);
//             setTimeout(() => setShake(false), 500);
//             toast.error('Please fill all fields correctly!', {
//                 position: "top-right",
//                 autoClose: 3000,
//                 hideProgressBar: false,
//                 closeOnClick: true,
//                 pauseOnHover: true,
//                 draggable: true,
//                 progress: undefined,
//             });
//             return;
//         }
        
//         // Add the new data point to the chart
//         const newLabels = [...chartData.labels, newDataPoint.year];
//         const newDatasetData = [...chartData.datasets[0].data, parseInt(newDataPoint.value)];
        
//         setChartData({
//             labels: newLabels,
//             datasets: [
//                 {
//                     ...chartData.datasets[0],
//                     data: newDatasetData
//                 }
//             ]
//         });
        
//         toast.success('Data point added successfully!', {
//             position: "top-right",
//             autoClose: 3000,
//             hideProgressBar: false,
//             closeOnClick: true,
//             pauseOnHover: true,
//             draggable: true,
//             progress: undefined,
//         });
        
//         // Reset form
//         setNewDataPoint({ year: '', value: '' });
//         setShowForm(false);
//     };

//     const handleCancel = (e) => {
//         if (e.target === e.currentTarget) {
//             setNewDataPoint({ year: '', value: '' });
//             setShowForm(false);
//         }
//     };

//     return (
//         <div className="max-w-2xl mx-auto p-4 relative">
//             <div className="relative flex items-center mb-4">
//                     <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//                     Land Price Prediction
//                     </h3>
//                     <button
//                       onClick={handleAddClick}
//                       className="ml-auto bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//                       title="Add Land Price Prediction"
//                     >
//                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//                         <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//                     </svg>
//                     </button>
//                   </div>

//             <Line data={chartData} options={options} />

//             {showForm && (
//                 <div 
//                     className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm transition-opacity duration-300"
//                     onClick={handleCancel}
//                 >
//                     <div
//                         className={`bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-xl w-full max-w-md relative border border-gray-700 shadow-2xl transform transition-all duration-300 ${shake ? 'animate-shake' : ''}`}
//                         onClick={e => e.stopPropagation()}
//                     >
//                         <button
//                             onClick={() => setShowForm(false)}
//                             className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200 p-1 rounded-full hover:bg-gray-700"
//                         >
//                             <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                             </svg>
//                         </button>

//                         <div className="mb-6 text-center">
//                             <h3 className="text-2xl font-bold text-white mb-1">Add Land Price Prediction</h3>
//                             <p className="text-gray-400 text-sm">Fill in the details to add to the chart</p>
//                         </div>

//                         <form onSubmit={handleSubmit} className="space-y-6">
//                             <div className="flex flex-col">
//                                 <label className="text-gray-300 text-sm font-medium mb-1 text-left">Year</label>
//                                 <input
//                                     type="number"
//                                     name="year"
//                                     value={newDataPoint.year}
//                                     onChange={handleInputChange}
//                                     className="px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
//                                     placeholder="e.g., 2026"
//                                     min="2000"
//                                     max="2100"
//                                 />
//                             </div>

//                             <div className="flex flex-col">
//                                 <label className="text-gray-300 text-sm font-medium mb-1 text-left">Projected Cost (₹)</label>
//                                 <div className="relative">
//                                     <input
//                                         type="number"
//                                         name="value"
//                                         value={newDataPoint.value}
//                                         onChange={handleInputChange}
//                                         className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all appearance-none"
//                                         placeholder="e.g., 25000"
//                                         min="0"
//                                         step="100"
//                                     />
//                                     <span className="absolute right-4 top-3 text-gray-400">₹</span>
//                                 </div>
//                             </div>

//                             <div className="flex justify-end space-x-3">
//                                 <button
//                                     type="button"
//                                     onClick={() => setShowForm(false)}
//                                     className="px-4 py-2 border border-gray-600 rounded-lg text-sm font-medium text-white bg-transparent hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
//                                 >
//                                     Cancel
//                                 </button>
//                                 <button
//                                     type="submit"
//                                     className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all shadow-lg hover:shadow-blue-500/30"
//                                 >
//                                     Add Projection
//                                 </button>
//                             </div>
//                         </form>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// }

// export default FutureGraph_ProjectDetail;

// import React from 'react';
// import {
//     Chart as ChartJS,
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend,
// } from 'chart.js';
// import { Line } from 'react-chartjs-2';

// // Register required chart components
// ChartJS.register(
//     CategoryScale,
//     LinearScale,
//     PointElement,
//     LineElement,
//     Title,
//     Tooltip,
//     Legend
// );

// const data = {
//     labels: ['2020', '2021', '2022', '2023', '2024', '2025'],
//     datasets: [
//         {
//             label: 'Cost Increase (₹)',
//             data: [10000, 12000, 14000, 16500, 19000, 22000],
//             fill: false,
//             borderColor: '#4F46E5',
//             tension: 0.3,
//         },
//     ],
// };

// const options = {
//     responsive: true,
//     plugins: {
//         legend: {
//             position: 'top',
//         },
//         title: {
//             display: true,
//             text: 'Year-wise Cost Increase',
//         },
//     },
// };

// function FutureGraph_ProjectDetail() {
//     return (
//         <div className="max-w-2xl mx-auto p-4">
//             <Line data={data} options={options} />
//         </div>
//     );
// }

// export default FutureGraph_ProjectDetail;
